import { useApolloClient } from '@apollo/client'
import {
  FetchViewQueryResultDocument,
  type ViewFieldsFragment,
  type ViewQueryResultCursorInput,
} from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { getOrganizationIdFromPath, useMe } from '@salescore/client-common'
import {
  type ChangeHistory,
  type HighlightConfigKey,
  updateViewQueryResult,
  useAppDispatch,
  useUpsertCachedView,
} from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  CORE_CONSTANT,
  getVisibleNodeNames,
  type ViewQueryField,
  type ViewQueryList,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { message } from 'antd'
import merge from 'lodash.merge'
import { type SetterOrUpdater, useRecoilState, useSetRecoilState } from 'recoil'

import { useGetQueryWithFilterTree } from '../../../hooks/useGetQueryWithFilterTree'
import { highlightConfigToDateString } from '../../../hooks/useHighlightConfig'
import { useMustacheParameter } from '../../../hooks/useMustacheParameter'
import { usePageSizeState } from '../../../hooks/usePageSizeState'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import { useViewStateHighlightConfigKey } from '../../../hooks/useViewState'
import { useRecordsImpl, useSetRecordsImpl } from '../../records'
import { changesAtom } from '../../recordsHistory'
import { resolveShouldJoinRecordsInApplication, useViewConfigSheet } from '../../viewConfig'
import { errorsAtom } from '../atoms'
import { useLoadingState } from '../hooks'
import { useUpdateSheetThreadsCache } from './useSheetThreads'

interface Save {
  kind: 'save'
}

interface Reset {
  kind: 'reset'
}

interface ChangeView {
  kind: 'changeView'
}

interface LoadNext {
  kind: 'loadNext'
}

interface AddColumn {
  kind: 'addColumn'
  fields: ViewQueryField[]
}

interface DeleteColumn {
  kind: 'deleteColumn'
  fields: ViewQueryField[]
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: 適当に定義した以下のインターフェースのプロパティの型をきちんと定義する
interface AddFilter {
  kind: 'addFilter'
  name: string
}

interface DeleteFilter {
  kind: 'deleteFilter'
  name: string
}

interface AddSort {
  kind: 'addSort'
  name: string
}

interface DeleteSort {
  kind: 'deleteSort'
  name: string
}

type RefetchReason =
  Save | Reset | ChangeView | LoadNext | AddColumn | DeleteColumn | AddFilter | DeleteFilter | AddSort | DeleteSort

interface OverrideParameters {
  highlightConfigKey?: HighlightConfigKey
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: writable selectorに移動
export const useRefetchMutation = (view: ViewFieldsFragment, query: ViewQueryList | undefined) => {
  const me = useMe()
  const dispatch = useAppDispatch()
  const config = useViewConfigSheet()
  const shouldJoinRecordsInApplication = resolveShouldJoinRecordsInApplication(me, config)
  const organizationId = getOrganizationIdFromPath()
  const mustacheParameter = useMustacheParameter()
  const getQueryWithFilterTree = useGetQueryWithFilterTree(query)
  const client = useApolloClient()
  const loading = useLoadingState()
  const [pageSize] = usePageSizeState()
  const highlightConfigKey = useViewStateHighlightConfigKey()
  const viewQueryResult = useViewQueryResult(view)
  const [changes, setChanges] = useRecoilState(changesAtom)
  const upsertCachedView = useUpsertCachedView()
  const updateSheetThreadsCache = useUpdateSheetThreadsCache(view)

  const setErrors = useSetRecoilState(errorsAtom)
  const records = useRecordsImpl()
  const setRecords = useSetRecordsImpl()

  const posthogProperties = {
    organizationId,
    organizationName: me.organization.name,
    viewId: view.id,
    viewType: view.type,
    viewName: view.name,
    userName: me.myUser.name,
    email: me.myUser.identity.email,
  }

  const fetchViewQueryResult = async ({
    cursor,
    useCache,
    override,
  }: {
    cursor: ViewQueryResultCursorInput
    useCache?: boolean
    override?: OverrideParameters
  }) => {
    const viewQuery = getQueryWithFilterTree()
    if (viewQuery === undefined) {
      return
    }
    const result = await client.query({
      query: FetchViewQueryResultDocument,
      variables: {
        organizationId,
        viewQuery,
        mustacheParameter,
        dateForDiffHighlight: highlightConfigToDateString(override?.highlightConfigKey ?? highlightConfigKey),
        cursor,
        // アプリケーションジョインをするかどうかは組織設定側で決めるが、
        // KPIセルをドリルダウンしたときは強制的にアプリケーションジョインを解除
        shouldJoinRecordsInApplication: view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)
          ? false
          : shouldJoinRecordsInApplication,
        viewId: view.id,
      },
      fetchPolicy: useCache === true ? 'cache-first' : 'network-only',
    })
    if (result.error !== undefined) {
      setErrors([JSON.stringify(result.error)])
      sendRefetchErrorToPosthog({
        ...posthogProperties,
        error: result.error,
      })
      return
    }
    setErrors([])

    const { viewQueryResult } = result.data
    const { error, warn } = viewQueryResult

    if (error !== undefined) {
      setErrors([error])
      sendRefetchErrorToPosthog({
        ...posthogProperties,
        error,
      })
      void message.error(error)
      return
    }
    if (warn === undefined) {
      setErrors([])
    } else {
      setErrors([warn])
      // warn は複数件が '\n' join された複合文字列で届きうるが、antd の message は
      // content を span に描画し、デフォルト CSS（white-space: normal）では '\n' が
      // スペースに潰れて 1 行連結表示になる。区切りを成立させるため行ごとに表示する
      warn.split('\n').forEach((line) => {
        void message.warning(line)
      })
    }

    return viewQueryResult
  }

  const fetch = async ({
    cursor,
    refetchReason,
    useCache,
    override,
  }: {
    cursor: ViewQueryResultCursorInput
    refetchReason: RefetchReason
    useCache?: boolean
    override?: OverrideParameters
    // eslint-disable-next-line sonarjs/cognitive-complexity
  }) => {
    // 次のいずれかの場合に fetch を抑止する (短絡評価):
    //  - query が undefined (kpi/kpiPivot view 等の list query を持たないケース)
    //  - compileSheetViewConfig が空 sheet config に対して返す emptyQuery (tree.name='salescore_users')
    // 文字列で判定しているのは、上流 (compileSheetViewConfig.ts) の emptyQuery とは
    // オブジェクト参照を共有できないため。上流が空時に undefined を返す形に揃えば、
    // この文字列 sentinel は不要になる。
    // salescore_users テーブルを root にする list query では誤判定するが、view 編集 UI 上では
    // salescore_users が root 選択肢に出てこないことを実機確認済みで、現時点で実害はない。
    // UI フィルターが外れて salescore_users root の view が作られた場合に問題化するので、
    // その時点で sentinel を破棄する必要がある。
    if (query === undefined || query.tree.name === 'salescore_users') {
      return
    }
    try {
      if (refetchReason.kind !== 'addColumn' && refetchReason.kind !== 'deleteColumn') {
        // 列の追加・削除ではローディングを挟まない
        loading.setTrue()
      }
      const viewQueryResult = await fetchViewQueryResult({ cursor, useCache, override })
      loading.setFalse()
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (!viewQueryResult) {
        return
      }
      dispatch(updateViewQueryResult({ viewId: view.id, incoming: viewQueryResult }))
      const viewQueryNodeResult = viewQueryResult.result
      const isInitialized = cursor.page === 1
      const mergeChangeReasons: Array<RefetchReason['kind']> = ['addColumn', 'deleteColumn', 'changeView']
      if (isInitialized) {
        if (mergeChangeReasons.includes(refetchReason.kind) && changes.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const latestSnapshotAfterChange = changes.at(-1)!.snapshotAfterChange // changes.length > 0 なので、snapshotAfterChangeは必ず存在する
          try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const latestSnapshotAfterChangeRecords = JSON.parse(latestSnapshotAfterChange) as ViewQueryRecordNode[]
            const newRecords = mergeRecords(query, viewQueryNodeResult, latestSnapshotAfterChangeRecords)
            setRecords(newRecords)
            void updateSheetThreadsCache(toRecordIds(newRecords))
            if (refetchReason.kind === 'deleteColumn') {
              // changes の中からの削除されたカラムの変更を削除する
              deleteChangesOnDeletedColumn(refetchReason, changes, setChanges)
            }
          } catch {
            setRecords(viewQueryNodeResult)
            void updateSheetThreadsCache(toRecordIds(viewQueryNodeResult))
            setChanges([])
          }
        } else {
          setRecords(viewQueryNodeResult)
          void updateSheetThreadsCache(toRecordIds(viewQueryNodeResult))
          setChanges([])
          // refetchが完了するよりも前にタブを切り替えた時のためにcachedViewも更新しておく
          upsertCachedView({ viewId: view.id, changes: [] })
        }
      } else {
        const newRecords = [...records, ...viewQueryNodeResult]
        setRecords(newRecords)
        void updateSheetThreadsCache(toRecordIds(newRecords))
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrors([error.message])
        sendRefetchErrorToPosthog({
          ...posthogProperties,
          error: error.message,
        })
      }
    } finally {
      loading.setFalse()
    }
  }

  return {
    async refetch(refetchReason: RefetchReason, useCache?: boolean, override?: OverrideParameters) {
      const defaultCursor: ViewQueryResultCursorInput = {
        page: 1,
        pageSize,
        chunkSize: 0,
      }
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: 複数ページになっているとき、全ページ分をrefetchする必要がある
      await fetch({
        cursor: defaultCursor,
        refetchReason,
        useCache,
        override,
      })
    },
    async loadNext() {
      if (viewQueryResult?.nextCursor === undefined) {
        return
      }
      await fetch({
        cursor: viewQueryResult.nextCursor,
        refetchReason: { kind: 'loadNext' },
      })
    },
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function deleteChangesOnDeletedColumn(
  refetchReason: DeleteColumn,
  changes: ChangeHistory[],
  setChanges: SetterOrUpdater<ChangeHistory[]>,
) {
  const deletedFieldNames = new Set(refetchReason.fields.map((x) => x.name))
  const validChangeHistories = []
  for (const changeHistory of changes) {
    const validChanges = []
    for (const change of changeHistory.changes) {
      if (change.deleted === true) {
        validChanges.push(change)
        continue
      }
      const validFieldChanges = []
      for (const fieldChange of change.fieldChanges) {
        if (!deletedFieldNames.has(fieldChange.fieldName)) {
          validFieldChanges.push(fieldChange)
        }
      }
      if (validFieldChanges.length > 0) {
        validChanges.push({ ...change, fieldChanges: validFieldChanges })
      }
    }
    if (validChanges.length > 0) {
      validChangeHistories.push({ ...changeHistory, changes: validChanges })
    }
  }
  setChanges(validChangeHistories)
}

const toRecordIds = (records: ViewQueryRecordNode[]): string[] =>
  records.map((record) => record.id).filter((id) => id !== undefined)

// records2 (行の増減があるかも) に存在しないプロパティを records1 (列の増減があるかも) で上書きする

export const mergeRecords = (
  viewQuery: ViewQueryList,
  records1: ViewQueryRecordNode[],
  records2: ViewQueryRecordNode[],
): ViewQueryRecordNode[] => {
  // getIdToNodesDict は records を破壊的に変更するため、ディープコピーを渡す
  const records1Copy = structuredClone(records1)
  const dict1 = getIdToNodesDict(viewQuery.tree.name, records1Copy)
  const records2Copy = structuredClone(records2)
  const dict2 = getIdToNodesDict(viewQuery.tree.name, records2Copy)

  // dict1 から dict2 に存在しないものを削除する
  for (const key of Object.keys(dict1)) {
    if (dict2[key] === undefined) {
      // records1 が、records2 が削除した子供のデータを含んでいる場合、
      // 当該子供を親のレコードからも削除しなくてはいけない
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      for (const recordAndParent of dict1[key]!) {
        // 絶対に一個以上の要素が存在する
        const { parent } = recordAndParent
        if (parent !== undefined) {
          for (const tableNode of parent.children) {
            tableNode.children = tableNode.children.filter(
              (childRecordNode) => childRecordNode.id !== recordAndParent.record.id,
            )
          }
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete dict1[key]
    }
  }
  const dict3 = merge(dict1, dict2)
  merge(dict2, dict3)
  const visibleNodeNames = getVisibleNodeNames(viewQuery)
  return records2Copy.map((record) => assignInnerRowGeometry(record, visibleNodeNames))
}

interface RecordAndParent {
  record: ViewQueryRecordNode
  parent: ViewQueryRecordNode | undefined
}

// ここで id は モデルID と レコードID の組み合わせである。
function getIdToNodesDict(rootModelName: string, records: ViewQueryRecordNode[]) {
  const dict: Record<string, RecordAndParent[]> = {}
  getIdToNodesDictInner(records, 0, dict, rootModelName)
  return dict
}

// records から id をキーにした辞書を作る破壊的な関数。
// 深すぎる階層は循環参照の可能性があるため探索しない
// eslint-disable-next-line @typescript-eslint/max-params
function getIdToNodesDictInner(
  records: ViewQueryRecordNode[],
  depth: number,
  dict: Record<string, RecordAndParent[]>,
  modelName: string,
  parent?: ViewQueryRecordNode,
) {
  if (depth > 10) {
    return
  }
  for (const record of records) {
    // 自分
    if (record.id === undefined) {
      continue
    }
    if (dict[`${modelName}-${record.id}`] === undefined) {
      // eslint-disable-next-line no-param-reassign
      dict[`${modelName}-${record.id}`] = [{ record, parent }]
    } else {
      dict[`${modelName}-${record.id}`]?.push({ record, parent })
    }
    // 子について再帰
    for (const tableNode of record.children) {
      for (const childRecordNode of tableNode.children) {
        getIdToNodesDictInner([childRecordNode], depth + 1, dict, tableNode.nodeName, record)
      }
    }
  }
}

export function sendRefetchErrorToPosthog({
  viewId,
  viewType,
  viewName,
  organizationId,
  organizationName,
  userName,
  email,
  error,
}: {
  viewId: string
  viewType: string
  viewName: string
  organizationId: string
  organizationName: string
  userName: string
  email: string
  error: unknown
}): void {
  Posthog.track(POSTHOG_EVENTS.failed_fetch_view_query_result, {
    viewId,
    viewType,
    viewName,
    organizationId,
    organizationName,
    userName,
    email,
    error,
  })
}
